/* eslint-disable @typescript-eslint/no-explicit-any */
import { assign, enqueueActions, setup } from 'xstate';

import type { BaseEvent, FlowActions, FlowContext, FlowEvent, FlowRoute } from '../types/machine';
import {
  generateStateTransitions,
  generateStates,
  getEventTypeByContext,
  getEventTypeByPath,
} from '../utils/FlowUtils';

export const createFlowMachine = <T extends FlowContext, TEvent extends BaseEvent>(
  routes: FlowRoute<T>[],
  actions: FlowActions<T, TEvent>,
  initialContext: T,
) =>
  setup({
    types: {
      context: {} as T,
      events: {} as FlowEvent<TEvent>,
    },
    actions: {
      setValues: assign(({ context, event }) => {
        if (event.type !== 'GOTO' && event.type !== 'COMPLETE') {
          return { ...context, ...event.values };
        }
        return context;
      }),
      ...actions,
    },
  }).createMachine({
    id: 'flow',
    initial: 'INIT',
    context: initialContext ?? {},
    on: {
      COMPLETE: {
        target: '.COMPLETED',
      },
      UPDATE_ROUTE: {
        actions: assign(({ context, event }) => {
          return {
            ...context,
            path: event.values?.path,
            pathPrevious: context.path,
            pathCategory: event.values?.pathCategory,
            hasPreviousPath: event.values?.hasPreviousPath,
          };
        }),
      },
      UPDATE_VALUES: {
        actions: ['setValues'],
      },
      GOTO: generateStateTransitions(routes),
    },
    states: {
      INIT: {
        on: {
          SET_INITIAL_CONTEXT: {
            actions: ['setValues'],
            target: 'IDLE',
          },
        },
      },
      IDLE: {
        on: {
          SET_ROUTE: {
            actions: enqueueActions(({ event, context, enqueue }: { event: any; context: any; enqueue: any }) => {
              enqueue('setValues');
              enqueue.raise({
                type: 'GOTO',
                stepName: event.values?.path
                  ? getEventTypeByPath(routes, event.values.path)
                  : getEventTypeByContext(routes, context),
              } as any);
            }),
          },
        },
      },
      COMPLETED: {
        type: 'final',
      },
      ...generateStates(routes),
    },
  } as any);
